import React, { useState } from 'react';
import { ControllableElement } from '@bynder-studio/render-core';
import { useTranslate } from '@bynder/localization';
import { notify } from '@bynder/design-system';
import ComponentModal from './ComponentModal';
import { ComponentModalMode } from './types';
import useComponents from '../useComponents';
import ComponentService, { AddComponentRequest } from 'packages/services/ComponentService';
import ComponentCollectionService from 'packages/services/ComponentCollectionService';

interface UseComponentModalProps {
    onSaveSuccess?: () => void;
}

const useComponentModal = ({ onSaveSuccess }: UseComponentModalProps) => {
    const { translate } = useTranslate();
    const [isOpen, setOpen] = useState(false);
    const [mode, setMode] = useState<ComponentModalMode>('create');
    const [selectedElements, setSelectedElements] = useState<ControllableElement[]>([]);
    const [isSaving, setIsSaving] = useState(false);

    const { createComponent } = useComponents();

    const onOpen = (modeProp: ComponentModalMode, selectedElements: ControllableElement[]) => {
        if (isOpen) {
            return;
        }

        if (selectedElements.length === 0) {
            return;
        }

        setMode(modeProp);
        setSelectedElements(selectedElements);
        setOpen(true);
    };

    const onClose = () => {
        setOpen(false);
        setSelectedElements([]);
    };

    const onSave = async ({
        name,
        description,
        collectionId,
        collectionName,
    }: {
        name: string;
        description: string;
        collectionId: string | null;
        collectionName?: string;
    }) => {
        let finalCollectionId: number | null = collectionId ? Number(collectionId) : null;

        if (collectionName) {
            const res = await ComponentCollectionService.addComponentCollection({
                name: collectionName,
            });

            if (res.status !== 200) {
                return;
            }

            finalCollectionId = res.json.id;
        }

        if (finalCollectionId === null) {
            return;
        }

        const preparedComponent = createComponent(selectedElements);

        const mergedComponent: AddComponentRequest = {
            name,
            description: description || undefined,
            collectionId: finalCollectionId,
            elements: preparedComponent.elements,
            textStyles: preparedComponent.textStyles || [],
            contentProperties: preparedComponent.contentProperties,
            type: preparedComponent.type,
        };

        setIsSaving(true);
        if (mode === 'create') {
            const res = await ComponentService.addComponent(mergedComponent);

            if (res.status === 200) {
                notify({
                    title: translate('components.create.modal.save.success'),
                    variant: 'success',
                });

                if (onSaveSuccess) {
                    onSaveSuccess();
                }
            } else {
                notify({
                    title: translate('components.create.modal.save.error.badRequest'),
                    variant: 'error',
                });
            }
        } else if (mode === 'edit') {
            // Additional logic for 'edit' mode can be added here
        }

        setIsSaving(false);
        setOpen(false);
        setSelectedElements([]);
    };

    const renderComponentModal = () => {
        return (
            <ComponentModal
                isOpen={isOpen}
                onClose={onClose}
                onSave={onSave}
                mode={mode}
                suggestedName={selectedElements.length === 1 ? selectedElements[0].name : 'New component'}
                isSaving={isSaving}
            />
        );
    };

    return {
        openComponentModal: onOpen,
        renderComponentModal,
    };
};

export default useComponentModal;
